import githubDarkDefault from "@shikijs/themes/github-dark-default";
import { Check, Copy } from "lucide-react";
import { useState } from "react";
import { createHighlighterCore } from "shiki";
import { createOnigurumaEngine } from "shiki/engine/oniguruma";
import { toast } from "sonner";
import { Button } from "~/components/ui/button";
import {
  CodeBlock,
  CodeBlockCodeHTML,
  CodeBlockGroup,
} from "~/components/ui/code-block";

const ExampleWebsiteHtml = `<html>
  <head>
    <!-- Paste the code here -->
  </head>
  <body>
    ...
  </body>
</html>`;

export async function getPixelCodeSnippets(scriptContent: string) {
  const highlighter = await createHighlighterCore({
    themes: [githubDarkDefault],
    langs: [() => import("@shikijs/langs/html")],
    engine: createOnigurumaEngine(import("shiki/wasm")),
  });

  const scriptContentHtml = highlighter.codeToHtml(scriptContent, {
    lang: "html",
    theme: "github-dark-default",
  });

  const exampleContentHtml = highlighter.codeToHtml(ExampleWebsiteHtml, {
    lang: "html",
    theme: "github-dark-default",
  });

  return {
    scriptContentHtml,
    exampleContentHtml,
  };
}

export function PixelInstructions({
  scriptContent,
  scriptContentHtml,
  exampleContentHtml,
  mode,
}: {
  scriptContent: string;
  scriptContentHtml: string;
  exampleContentHtml: string;
  mode: "guide" | "verify_installation";
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(scriptContent);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const headingStyle = mode === "guide" ? "text-base" : "text-sm";

  return (
    <ol
      className={`space-y-6 list-decimal marker:text-${headingStyle} text-sm [&>li]:ml-4`}
    >
      <li>
        <h3 className={`${headingStyle} font-medium`}>Copy the code snippet</h3>
        <p className="mt-1 text-muted-foreground">
          Add our script to your website to show your visitors personalized
          variants, and to track conversions.
        </p>

        <CodeBlock className="mt-3">
          <CodeBlockGroup className="border-border border-b px-4 py-2">
            <div className="flex items-center gap-2">
              <div className="bg-primary/10 text-primary rounded px-2 py-1 text-xs font-medium">
                HTML
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleCopy}
            >
              {copied ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </CodeBlockGroup>
          <CodeBlockCodeHTML inlineHTML={scriptContentHtml} />
        </CodeBlock>
      </li>

      <li>
        <h3 className={`${headingStyle} font-medium`}>
          Paste the code into the {"<head>"} tag of your website
        </h3>
        <p className="mt-1 text-muted-foreground">
          Make sure our code is added at the very top of the{" "}
          <code>{"<head>"}</code> tag. This ensures our script is loaded early
          and works properly.
        </p>
        <CodeBlock className="mt-3">
          <CodeBlockGroup className="border-border border-b px-4 py-2">
            <div className="flex items-center gap-2">
              <div className="bg-primary/10 text-primary rounded px-2 py-1 text-xs font-medium">
                HTML
              </div>
            </div>
          </CodeBlockGroup>
          <CodeBlockCodeHTML inlineHTML={exampleContentHtml} />
        </CodeBlock>
      </li>
      {mode === "verify_installation" && (
        <li>
          <h3 className={`${headingStyle} font-medium`}>
            Verify the installation
          </h3>
          <p className="mt-1 text-muted-foreground">
            Click Verify Installation below, and we'll guide you through the
            final steps.
          </p>
        </li>
      )}
    </ol>
  );
}
